/* ──────────────────────────────────────────────────────────────────────
   Anchor scroller — handles in-page nav links to elements with
   [data-anchor="..."] (Events / Player Profiles / Order of Merit).

   The cover-reveal and horizontal-scroll sections pin their content with
   transforms and sticky wrappers, so the browser's native scroll-to-id
   behaviour lands in the wrong place. This script intercepts the click,
   calculates the right scroll position, and runs a manual rAF tween
   (native scrollTo({behavior:'smooth'}) is intercepted by GSAP on this
   theme).
   ────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var HEADER_OFFSET = 90;

  /* ── Smooth scroll (manual rAF tween) ───────────────────────────── */
  var raf = null;
  function smoothScrollTo(targetY) {
    if (raf) cancelAnimationFrame(raf);
    var startY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var dist   = targetY - startY;
    if (Math.abs(dist) < 4) { window.scrollTo(0, targetY); return; }
    var dur    = Math.min(900, 320 + Math.abs(dist) * 0.3);
    var t0     = null;
    function step(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      var e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
      window.scrollTo(0, Math.round(startY + dist * e));
      if (p < 1) raf = requestAnimationFrame(step);
      else raf = null;
    }
    raf = requestAnimationFrame(step);
  }

  function findScrollWrapper(el) {
    var hs = el.closest('[data-hscroll]');
    if (hs) return { kind: 'hs', wrapper: hs };
    var cv = el.closest('[data-cover-reveal]');
    if (cv) return { kind: 'cv', wrapper: cv };
    return null;
  }

  function indexOfPanel(wrapper, sel, target) {
    var panels = wrapper.querySelectorAll(sel);
    for (var i = 0; i < panels.length; i++) {
      if (panels[i] === target || panels[i].contains(target)) return i;
    }
    return 0;
  }

  function scrollToCV(wrapper, target) {
    var panels = wrapper.querySelectorAll('[data-cv-panel]');
    var wrapperTop = wrapper.getBoundingClientRect().top + window.scrollY;
    if (!panels.length) {
      return smoothScrollTo(wrapperTop - HEADER_OFFSET);
    }
    /* With < 2 panels the cover-reveal JS bails out and never pins the
       wrapper or expands its height — the single panel renders inline at
       100vh, so just land at the section top. */
    if (panels.length < 2) {
      return smoothScrollTo(wrapperTop - HEADER_OFFSET);
    }
    var idx = indexOfPanel(wrapper, '[data-cv-panel]', target);
    var speed = parseInt(wrapper.dataset.cvSpeed, 10) || 200;
    var hold  = parseInt(wrapper.dataset.cvHold,  10) || 80;
    var total = hold + speed * (panels.length - 1) + hold;
    var vh    = window.innerHeight;

    if (window.innerWidth <= 768) {
      return smoothScrollTo(panels[idx].getBoundingClientRect().top + window.scrollY - HEADER_OFFSET);
    }

    var holdFrac = hold / total;
    var moveFrac = 1 - 2 * holdFrac;
    var n        = Math.max(1, panels.length - 1);
    var start    = holdFrac + (idx - 1) * (moveFrac / n);
    var end      = start    + (moveFrac / n);
    var progress = idx === 0 ? holdFrac * 0.5 : start + (end - start) * 0.7;
    var y        = wrapperTop + progress * total * vh / 100;
    smoothScrollTo(Math.max(0, y - HEADER_OFFSET / 2));
  }

  function scrollToHS(wrapper) {
    var y = wrapper.getBoundingClientRect().top + window.scrollY;
    smoothScrollTo(Math.max(0, y - HEADER_OFFSET / 2));
  }

  function scrollToTarget(target) {
    var ctx = findScrollWrapper(target);
    if (!ctx) {
      smoothScrollTo(Math.max(0, target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET));
      return;
    }
    if (ctx.kind === 'cv') scrollToCV(ctx.wrapper, target);
    else scrollToHS(ctx.wrapper);
  }

  function findTarget(name) {
    name = String(name || '').replace(/^#/, '');
    if (!name) return null;
    try {
      return document.querySelector('[data-anchor="' + CSS.escape(name) + '"]');
    } catch (e) {
      return document.querySelector('[data-anchor="' + name.replace(/"/g, '\\"') + '"]');
    }
  }

  function handleHash(name) {
    var target = findTarget(name);
    if (!target) return false;
    scrollToTarget(target);
    return true;
  }

  /* ── Re-bind on every nav link directly ─────────────────────────── */
  function bindLinks() {
    var links = document.querySelectorAll('a[href]');
    Array.prototype.forEach.call(links, function (a) {
      if (a.__anchorBound) return;
      var href = a.getAttribute('href') || '';
      var hashIdx = href.indexOf('#');
      if (hashIdx < 0) return;
      var hash = href.slice(hashIdx + 1);
      if (!hash) return;
      var pathBefore = href.slice(0, hashIdx);
      var samePath = pathBefore === '' || pathBefore === '/' ||
                     pathBefore === location.pathname;
      /* if href has no path (e.g. just "#events") AND we're not on the
         homepage where the target lives, rewrite to navigate home — that
         way nav links work from any page on the site */
      if (samePath && pathBefore === '' && !findTarget(hash) && location.pathname !== '/') {
        a.setAttribute('href', '/#' + hash);
        return;
      }
      if (!samePath) return;
      if (!findTarget(hash)) return;
      a.__anchorBound = true;
      /* attach in capture phase on the A itself so we beat magnetic-hover
         + btn-slide internal handlers.

         We've found that something in this theme silently swallows the
         in-page scroll (GSAP scroll-trigger? section-stack? — debugging
         the interaction proved fruitless). So instead of doing the scroll
         in-page, we force a full-page reload to the same URL with the
         hash attached. The bootHash path on the next page-load is rock
         solid — it actually scrolls — so this gives us a guaranteed UX
         at the cost of one extra navigation. */
      a.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var u = new URL(window.location.href);
        u.hash = '#' + hash;
        /* cache-bust so the browser treats this as a real navigation
           rather than a same-URL hash-only change */
        u.searchParams.set('_a', Date.now().toString(36).slice(-4));
        window.location.assign(u.pathname + u.search + u.hash);
      }, true);
    });
  }

  /* ── Init ───────────────────────────────────────────────────────── */
  function init() {
    bindLinks();
    /* clean any "_a=" parameter we used to force a full-page navigation */
    if (window.location.search.indexOf('_a=') !== -1) {
      try {
        var u = new URL(window.location.href);
        u.searchParams.delete('_a');
        history.replaceState(null, '', u.pathname + (u.search || '') + u.hash);
      } catch (e) {}
    }
    if (location.hash) {
      var tries = 0;
      function tryHash() {
        if (handleHash(location.hash) || tries > 12) return;
        tries++;
        setTimeout(tryHash, 120);
      }
      tryHash();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-bind when Shopify reloads sections in the editor */
  document.addEventListener('shopify:section:load', bindLinks);

  /* hashchange handler — use property assignment AS WELL AS addEventListener
     in case the theme's earlier scripts have wrapped addEventListener */
  var onHashChange = function () { handleHash(location.hash); };
  window.addEventListener('hashchange', onHashChange);
  var prevOnHashChange = window.onhashchange;
  window.onhashchange = function (e) {
    if (typeof prevOnHashChange === 'function') {
      try { prevOnHashChange.call(this, e); } catch (err) {}
    }
    onHashChange();
  };

  /* Belt-and-braces: re-walk the DOM every couple of seconds for the
     first few seconds, in case other scripts mutate the nav late */
  var passes = 0;
  var poll = setInterval(function () {
    bindLinks();
    if (++passes >= 6) clearInterval(poll);
  }, 500);
})();
/* v1781182185 */
